import { useState } from "react";
import { createUser, deleteUser, fetchAllUsers, updateUser } from "../api/users";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IoClose } from "react-icons/io5"


const Users = () => {
    const queryClient = useQueryClient();
    const [query, setQuery] = useState("");
    const [sheetOpen, setSheetOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [formName, setFormName] = useState('');
    const [formTelegramId, setFormTelegramId] = useState('');
    const [formIsAdmin, setFormIsAdmin] = useState(false);
    const [currentTelegramId, setCurrentTelegramId] = useState(null);

    const { data: users = [], isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: fetchAllUsers
    });
    
    const filteredUsers = users.filter((u) => `${u.name}`.toLowerCase().includes(query.toLowerCase()));

    const handleAddOpen = () => {
        setFormName('')
        setFormTelegramId('')
        setFormIsAdmin(false)
        setCurrentTelegramId(null)
        setModalMode('add')
        setSheetOpen(true)
    };

    const handleEditOpen = (user) => {
        setFormName(user.name)
        setFormTelegramId(user.telegram_id)
        setFormIsAdmin(user.is_admin)
        setCurrentTelegramId(user.telegram_id)
        setModalMode('edit')
        setSheetOpen(true)
    };

    const handleSubmit = () => {
        const fn = modalMode === 'add'
            ? createUser({ telegram_id: parseInt(formTelegramId), name: formName, is_admin: formIsAdmin})
            : updateUser(currentTelegramId, { new_telegram_id: parseInt(formTelegramId), new_name: formName, is_admin: formIsAdmin});
        fn.then(() => {
            setSheetOpen(false);
            queryClient.invalidateQueries({ queryKey: ['users']});
        });
    };

    const handleDelete = (telegram_id) => {
        if (!window.confirm('Delete this member?')) return;
        deleteUser(telegram_id).then(() => queryClient.invalidateQueries({ queryKey: ['users']}));
    };

    return (
        <div className="flex flex-col w-full max-w-md mx-auto px-3 pt-4 pb-24">
            
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-white text-3xl font-bold">Team</h1>
                <button 
                onClick={handleAddOpen}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-secondary-800 text-white text-2xl font-bold">+</button>
            </div>
            
            {/* Search */}
            <input
                type="text"
                placeholder="Search members..."
                value={query}
                onChange={(e)=> setQuery(e.target.value)}
                className="w-full bg-primary-800 text-white px-4 py-3 rounded-xl border border-primary-50 
                focus:outline-none focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800
                placeholder:text-primary-50 text-sm mb-3"
                />
            <p className="text-primary-50 text-xs font-mono mb-3">{filteredUsers.length} {filteredUsers.length === 1 ? 'MEMBER' : 'MEMBERS'}</p>

            {/* Users List */}
            {isLoading ? (
                <p className="text-primary-50 text-sm text-center mt-8">Loading...</p>
            ) : filteredUsers.length === 0 ? (
                <p className="text-primary-50 text-sm text-center mt-8">No members found.</p>
            ) : (
                <div className="flex flex-col gap-3">
                {filteredUsers.map((user, index) => (
                    <div key={index} className=" bg-primary-800 border border-primary-50 rounded-2xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-secondary-50 flex items-center justify-center
                            text-secondary-800 text-sm font-bold font-mono shrink-0">
                                {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <div>
                            <p className="text-white font-medium text-sm">{user.name}</p>
                            <p className="text-white text-xs font-mono">ID: {user.telegram_id}</p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => 
                                handleEditOpen(user)}
                            className="border border-secondary-800 text-secondary-800 bg-secondary-50 rounded-xl px-3 py-1.5 text-xs font-mono">
                            EDIT
                        </button>
                        <button
                            onClick={() => 
                                handleDelete(user.telegram_id)}
                            className="border border-danger-800 text-danger-800 bg-danger-50 rounded-xl px-3 py-1.5 text-xs font-mono">
                            DELETE
                        </button>
                    </div>
                </div>
                ))}
            </div>
        )}
            
            {/* User Modal */}
            {sheetOpen && (
                <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setSheetOpen(false)}>
                    <div className="absolute bottom-0 left-0 right-0 bg-background-400 border-t border-primary-50 rounded-t-2xl px-5 pt-3 pb-10" 
                        onClick={e => e.stopPropagation()}>
                        {/* Drag handle */}
                        <div className="w-9 h-1 bg-primary-600 rounded-full mx-auto mb-5" />
                        
                        {/* Sheet header */}
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-white text-base font-semibold">{modalMode === 'add' ? 'New Member' : 'Edit Member'}</h3>
                            <button onClick={() => setSheetOpen(false)}>
                                <IoClose className="text-primary-50 text-xl" />
                            </button>
                        </div>

                        {/* Name */}
                        <div className="mb-4">
                            <label className="block text-xs text-primary-50 font-mono mb-1.5">NAME</label>
                            <input 
                                type='text'
                                placeholder="Member's Name"
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm placeholder:text-primary-10 focus:outline-none 
                                focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                        </div>
                        {/* Telegram ID */}
                        <div className="mb-4">
                            <label className="block text-xs text-primary-50 font-mono mb-1.5">TELEGRAM ID</label>
                            <input 
                                type='number'
                                inputMode='numeric'
                                placeholder="e.g. 123456789"
                                value={formTelegramId}
                                onChange={(e) => setFormTelegramId(e.target.value)}
                                className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm placeholder:text-primary-10 focus:outline-none 
                                focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                        </div>
                        {/* Is Admin */}
                        <div className="flex items-center justify-between mb-4 bg-background-600 rounded-xl px-4 py-3">
                            <div>
                                <p className="text-white text-sm font-mono">Admin Access</p>
                                <p className="text-primary-50 text-xs">Can access the dashboard</p>
                            </div>
                            <div
                                onClick={() => setFormIsAdmin(!formIsAdmin)}
                                className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 flex items-center px-1 ${
                                    formIsAdmin ? 'bg-success-800' : 'bg-primary-50'
                                }`}>
                                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                    formIsAdmin ? 'translate-x-6' : 'translate-x-0'
                                }`}/>
                            </div>
                        </div>
                        {/* Actions */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setSheetOpen(false)}
                                className="flex-1 bg-primary-800 text-primary-50 rounded-xl py-3 text-xs font-mono">
                                CANCEL
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="flex-1 bg-secondary-800 text-white rounded-xl py-3 text-xs font-mono font-bold">
                                {modalMode === 'add' ? 'ADD MEMBER' : 'SAVE CHANGES'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
    </div>
    );
};

export default Users;